import AiCameraCapture from './AiCameraCapture';
import { CameraIcon, ClipboardIcon, UploadIcon } from './AiPhotoIcons';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import './AiPresetImportModal.css';
import {
  AI_PRESET_MAX_EXTRA,
  AI_PRESET_MAX_IMAGES,
  requestAiPresetExtract,
  resizeImageFile,
  type AiCalendarResult,
  type AiCrossTopicRow,
  type AiFixedProgramRow,
  type AiPresetImage,
  type AiPresetKind,
  type AiScheduleResult,
  type AiTrackRow,
} from '../../utils/aiPresetExtract';
import { newDraftId, type FixedProgramDraft } from '../../utils/fixedProgramDrafts';
import { RoundCheck } from './FixedProgramFields';
import FixedProgramBulkTable, { type FixedProgramBulkHandle } from './FixedProgramBulkTable';

export type AiClassOption = { id: string; label: string; grades?: string[] };

/** 학사일정 반영 결과 */
export type AiCalendarApply = {
  settings: {
    academicYear: number;
    yearStartsOn: string;
    yearEndsOn: string;
    summerBreakStartsOn: string;
    summerBreakEndsOn: string;
    winterBreakStartsOn: string;
    winterBreakEndsOn: string;
  };
  days: Array<{ day: string; day_kind: 'holiday' | 'closure'; note: string }>;
  /** 미리보기에는 있었으나 방학·주말·학년 범위 밖으로 빠져 반영하지 않는 날짜 */
  omittedDays: string[];
  programs: FixedProgramDraft[];
};

type CommonProps = {
  title: string;
  defaultGrade: number;
  schoolYear: number;
  onClose: () => void;
  /** 이미 열린 모달 위에 겹칠 때 배경 z-index 클래스 */
  backdropClassName?: string;
  /** 개발·스크린샷용: 이 결과로 미리보기부터 시작 */
  initialResult?: unknown;
  /** 같은 버튼으로 다시 열 때 이전 결과를 구분하는 로컬 키. 없으면 kind만 사용 */
  resumeKey?: string;
};
type Props =
  | (CommonProps & {
      kind: 'calendar';
      /** 학년도 범위의 자동 공휴일 */
      autoHolidays: (year: number) => Array<{ day: string; note: string }>;
      classOptions: AiClassOption[];
      onApply: (r: AiCalendarApply) => void;
    })
  | (CommonProps & { kind: 'schedule'; onApply: (r: AiScheduleResult) => void })
  | (CommonProps & {
      kind: 'tracks';
      /** 기본 편제 예시(이름 → 학기 구분/통합) */
      presetTermScope: (grade: number, name: string) => 'split' | 'unified' | null;
      /** 섹션에 이미 있는 편제명 — 같은 이름은 덮어쓰지 않고 그대로 둠 */
      existingTrackNames?: string[];
      onApply: (rows: AiTrackRow[]) => void;
    })
  | (CommonProps & {
      kind: 'crossTopics';
      /** 이미 등록된 주제어 — 같은 이름은 시수만 갱신 */
      existingTopics?: string[];
      onApply: (rows: AiCrossTopicRow[]) => void;
    })
  | (CommonProps & {
      kind: 'fixedPrograms';
      workspaceId: string;
      classOptions: AiClassOption[];
      onApply: (rows: FixedProgramDraft[]) => void;
    });

const KIND_HINT: Record<AiPresetKind, string> = {
  calendar: '학사일정표 사진을 넣어 주세요. 학년도·방학과 공휴일·휴업일만 읽어요.',
  schedule: '일과 시간표(교시별 시각)·요일별 수업 시수 사진을 넣어 주세요. 정규 교시 뒤는 방과후(시간 없음)로 기록해요.',
  tracks: '교육과정 편제표·시수 배당표 사진을 넣어 주세요. 본교 최종 기준시수(증감 반영)를 읽어요.',
  crossTopics: '범교과 주제 계획표 사진을 넣어 주세요. 주제어와 1·2학기 편성 시수를 읽어 등록해요.',
  fixedPrograms: '행사 계획 사진을 넣어 주세요. 보이는 날짜·이름·교시·시수만 채우고, 없는 칸은 비워 둡니다.',
};

function classIdsForGrade(options: AiClassOption[], grade: number): string[] {
  const token = String(grade);
  return options.filter((c) => (c.grades ?? []).some((g) => String(g) === token)).map((c) => c.id);
}

function fixedRowsFromExtract(rows: AiFixedProgramRow[], gradeIds: string[]): FixedProgramDraft[] {
  return rows
    .filter((row) => row.name.trim() || row.date)
    .map((row) => ({
      id: newDraftId(),
      date: row.date,
      name: row.name,
      periods: [...(row.periods ?? []).map((n) => `class:${n}`), ...(row.after_school ?? []).map((n) => `after_school:${n}`)],
      hours: row.hours > 0 ? row.hours : undefined,
      timetableName: '',
      startTime: row.start_time,
      endTime: row.end_time,
      gradeIds: [...gradeIds],
      source: 'ai' as const,
      updatedAt: Date.now(),
    }));
}

type AiPreviewStore = {
  v: 1;
  kind: AiPresetKind;
  grade: number;
  notes: string;
  calendar: CalendarEdit | null;
  schedule: AiScheduleResult | null;
  tracks: AiTrackRow[] | null;
  crossTopics: AiCrossTopicRow[] | null;
  fixedPrograms: FixedProgramDraft[] | null;
};

const previewStorageKey = (kind: AiPresetKind, resumeKey?: string) => `cp-ai-preset-preview.v1:${resumeKey || kind}`;

function previewHasBody(kind: AiPresetKind, row: AiPreviewStore): boolean {
  if (row.v !== 1 || row.kind !== kind) return false;
  if (kind === 'calendar') return !!row.calendar;
  if (kind === 'schedule') return !!row.schedule;
  if (kind === 'tracks') return Array.isArray(row.tracks);
  if (kind === 'crossTopics') return Array.isArray(row.crossTopics);
  return Array.isArray(row.fixedPrograms);
}

function readAiPreview(kind: AiPresetKind, resumeKey?: string): AiPreviewStore | null {
  try {
    const raw = JSON.parse(localStorage.getItem(previewStorageKey(kind, resumeKey)) || 'null') as AiPreviewStore | null;
    if (!raw || !previewHasBody(kind, raw)) return null;
    return raw;
  } catch {
    return null;
  }
}

function writeAiPreview(kind: AiPresetKind, resumeKey: string | undefined, row: Omit<AiPreviewStore, 'v' | 'kind'>) {
  try {
    const payload: AiPreviewStore = { v: 1, kind, ...row };
    if (!previewHasBody(kind, payload)) return;
    localStorage.setItem(previewStorageKey(kind, resumeKey), JSON.stringify(payload));
  } catch {
    /* 용량 초과 등 임시 저장 실패는 화면 흐름을 막지 않음 */
  }
}

const AiPresetImportModal: React.FC<Props> = (props) => {
  const { kind, title, defaultGrade, schoolYear, onClose } = props;
  const [images, setImages] = useState<AiPresetImage[]>([]);
  const [grade, setGrade] = useState<number>(Math.min(6, Math.max(1, defaultGrade || 1)));
  const [extra, setExtra] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const init = props.initialResult;
  const [calendar, setCalendar] = useState<CalendarEdit | null>(() =>
    init && props.kind === 'calendar' ? buildCalendarEdit(init as AiCalendarResult, props.autoHolidays) : null
  );
  const [schedule, setSchedule] = useState<AiScheduleResult | null>(() => (init && props.kind === 'schedule' ? (init as AiScheduleResult) : null));
  const [tracks, setTracks] = useState<AiTrackRow[] | null>(() =>
    init && props.kind === 'tracks' ? (init as { tracks: AiTrackRow[] }).tracks : null
  );
  const [crossTopics, setCrossTopics] = useState<AiCrossTopicRow[] | null>(() =>
    init && props.kind === 'crossTopics' ? (init as { topics: AiCrossTopicRow[] }).topics : null
  );
  const [fixedPrograms, setFixedPrograms] = useState<FixedProgramDraft[] | null>(() =>
    init && props.kind === 'fixedPrograms'
      ? fixedRowsFromExtract(
          (init as { programs: AiFixedProgramRow[] }).programs ?? [],
          classIdsForGrade(props.classOptions, Math.min(6, Math.max(1, defaultGrade || 1)))
        )
      : null
  );
  const [hasSaved, setHasSaved] = useState(() => !!readAiPreview(kind, props.resumeKey));
  const fixedPreviewRef = useRef<FixedProgramBulkHandle | null>(null);
  const [notes, setNotes] = useState('');
  const fileRef = useRef<HTMLInputElement | null>(null);
  const camRef = useRef<HTMLInputElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => () => abortRef.current?.abort(), []);
  const addFileListRef = useRef<(files: File[]) => Promise<void>>(async () => undefined);

  const hasResult = !!(calendar || schedule || tracks || crossTopics || fixedPrograms);
  const [camOpen, setCamOpen] = useState(false);
  const [clipMsg, setClipMsg] = useState<string | null>(null);

  const addFiles = async (files: FileList | null) => {
    if (!files) return;
    await addFileList(Array.from(files));
  };
  /** 여러 장이면 다음 빈 칸부터 차례로 채움 — 항상 긴 변 1568px · JPEG 0.82로 축소(resizeImageFile) */
  const addFileList = async (files: File[]) => {
    setError(null);
    const room = AI_PRESET_MAX_IMAGES - images.length;
    const list = files.filter((f) => f.type.startsWith('image/')).slice(0, Math.max(0, room));
    if (files.length > room) setError(`사진은 최대 ${AI_PRESET_MAX_IMAGES}장까지 넣을 수 있어요.`);
    try {
      const out = await Promise.all(list.map(resizeImageFile));
      setImages((prev) => [...prev, ...out].slice(0, AI_PRESET_MAX_IMAGES));
    } catch (e) {
      setError(e instanceof Error ? e.message : '사진을 읽지 못했습니다.');
    }
  };

  /** 클립보드 이미지 → 다음 빈 칸 */
  const pasteFromClipboard = async () => {
    setClipMsg(null);
    const cb = navigator.clipboard as Clipboard | undefined;
    if (!cb?.read) {
      setClipMsg('이 브라우저는 클립보드 읽기를 지원하지 않아요. Ctrl+V(⌘+V)로 붙여 넣어 주세요.');
      return;
    }
    try {
      const items = await cb.read();
      const files: File[] = [];
      for (const it of items) {
        const t = it.types.find((x) => x.startsWith('image/'));
        if (!t) continue;
        const blob = await it.getType(t);
        files.push(new File([blob], `clipboard-${Date.now()}.${t.split('/')[1] || 'png'}`, { type: t }));
      }
      if (files.length === 0) {
        setClipMsg('클립보드에 이미지가 없어요. 사진을 복사한 뒤 다시 눌러 주세요.');
        return;
      }
      await addFileList(files);
    } catch (e) {
      const denied = e instanceof DOMException && (e.name === 'NotAllowedError' || e.name === 'SecurityError');
      setClipMsg(denied ? '클립보드 접근이 허용되지 않았어요. 브라우저에서 권한을 허용하거나 Ctrl+V(⌘+V)로 붙여 넣어 주세요.' : '클립보드에서 이미지를 읽지 못했어요.');
    }
  };

  addFileListRef.current = addFileList;
  /** Ctrl+V(⌘+V) 붙여넣기 → 다음 빈 칸 (결과 화면·입력칸 편집 중에는 무시) */
  useEffect(() => {
    if (hasResult) return;
    const onPaste = (e: ClipboardEvent) => {
      const tgt = e.target as HTMLElement | null;
      if (tgt && (tgt.tagName === 'TEXTAREA' || tgt.tagName === 'INPUT')) return;
      const files = Array.from(e.clipboardData?.files ?? []).filter((f) => f.type.startsWith('image/'));
      if (files.length === 0) return;
      e.preventDefault();
      setClipMsg(null);
      void addFileListRef.current(files);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [hasResult]);

  const run = async () => {
    if (images.length === 0) {
      setError('사진을 1장 이상 넣어 주세요.');
      return;
    }
    setBusy(true);
    setError(null);
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      const res = await requestAiPresetExtract({ kind, grade, schoolYear, extra, images }, ac.signal);
      setNotes(String((res as { notes?: string }).notes ?? ''));
      if (props.kind === 'calendar') setCalendar(buildCalendarEdit(res as AiCalendarResult, props.autoHolidays));
      else if (props.kind === 'schedule') setSchedule(res as AiScheduleResult);
      else if (props.kind === 'tracks')
        setTracks(
          (res as { tracks: AiTrackRow[] }).tracks.map((t) => ({
            ...t,
            short_name: t.short_name || t.name.slice(0, 3),
            term_scope: props.presetTermScope(grade, t.name) ?? t.term_scope,
          }))
        );
      else if (props.kind === 'crossTopics') setCrossTopics((res as { topics: AiCrossTopicRow[] }).topics ?? []);
      else if (props.kind === 'fixedPrograms') {
        setFixedPrograms(fixedRowsFromExtract((res as { programs: AiFixedProgramRow[] }).programs ?? [], classIdsForGrade(props.classOptions, grade)));
      }
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return;
      setError(e instanceof Error ? e.message : 'AI 결과를 받지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const snapshotPreview = (programs: FixedProgramDraft[] | null) => {
    writeAiPreview(kind, props.resumeKey, {
      grade,
      notes,
      calendar: kind === 'calendar' ? calendar : null,
      schedule: kind === 'schedule' ? schedule : null,
      tracks: kind === 'tracks' ? tracks : null,
      crossTopics: kind === 'crossTopics' ? crossTopics : null,
      fixedPrograms: kind === 'fixedPrograms' ? programs : null,
    });
    setHasSaved(true);
  };

  useEffect(() => {
    if (!hasResult) return;
    snapshotPreview(kind === 'fixedPrograms' ? (fixedPreviewRef.current?.getDrafts() ?? fixedPrograms) : null);
    // 결과·수정이 바뀔 때마다 이 브라우저에만 임시 저장
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasResult, calendar, schedule, tracks, crossTopics, fixedPrograms, notes, grade]);

  const requestClose = () => {
    if (hasResult) snapshotPreview(kind === 'fixedPrograms' ? (fixedPreviewRef.current?.getDrafts() ?? fixedPrograms) : null);
    onClose();
  };

  const showPrevious = () => {
    const row = readAiPreview(kind, props.resumeKey);
    if (!row) {
      setHasSaved(false);
      return;
    }
    setError(null);
    setGrade(Math.min(6, Math.max(1, row.grade || 1)));
    setNotes(row.notes || '');
    setCalendar(kind === 'calendar' ? row.calendar : null);
    setSchedule(kind === 'schedule' ? row.schedule : null);
    setTracks(kind === 'tracks' ? row.tracks : null);
    setCrossTopics(kind === 'crossTopics' ? row.crossTopics : null);
    setFixedPrograms(kind === 'fixedPrograms' ? row.fixedPrograms : null);
  };

  const backToInput = () => {
    if (hasResult) snapshotPreview(kind === 'fixedPrograms' ? (fixedPreviewRef.current?.getDrafts() ?? fixedPrograms) : null);
    setCalendar(null);
    setSchedule(null);
    setTracks(null);
    setCrossTopics(null);
    setFixedPrograms(null);
  };

  const apply = () => {
    if (props.kind === 'calendar' && calendar) props.onApply(calendarToApply(calendar));
    else if (props.kind === 'schedule' && schedule) props.onApply(schedule);
    else if (props.kind === 'tracks' && tracks) props.onApply(tracks.filter((t) => t.name.trim()));
    else if (props.kind === 'crossTopics' && crossTopics) props.onApply(crossTopics.filter((t) => t.topic.trim()));
    else if (props.kind === 'fixedPrograms') {
      const rows = fixedPreviewRef.current?.getDrafts() ?? fixedPrograms ?? [];
      props.onApply(rows.filter((row) => !row.deleted && (row.name.trim() || row.date)));
    }
    requestClose();
  };

  return (
    <div className={`cp-modal-backdrop${props.backdropClassName ? ` ${props.backdropClassName}` : ''}`} onClick={busy ? undefined : requestClose}>
      <div className={`cp-modal cp-aip-modal cp-fpb-modal-like${fixedPrograms ? ' cp-fpb-modal' : ''}`} role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <div className="cp-aip-head">
          <h3>{title}</h3>
          <button type="button" className="cp-annual-timetable-settings-modal-close cp-fpb-x" onClick={requestClose} aria-label="닫기" disabled={busy}>
            ×
          </button>
        </div>
        <div className="cp-modal-body cp-aip-body">
          {!hasResult ? (
            <>
              <p className="cp-aip-hint">{KIND_HINT[kind]}</p>
              <div className="cp-aip-block">
                <div className="cp-aip-label">
                  자료 사진 <span className="cp-aip-count">{images.length}/{AI_PRESET_MAX_IMAGES}</span>
                </div>
                <div className="cp-aip-photos">
                  {Array.from({ length: AI_PRESET_MAX_IMAGES }, (_, i) => {
                    const im = images[i];
                    if (im)
                      return (
                        <div key={i} className="cp-aip-photo">
                          <img src={im.previewUrl} alt={`자료 ${i + 1}`} />
                          <span className="cp-aip-photo-no">{i + 1}</span>
                          <button
                            type="button"
                            className="cp-aip-photo-x"
                            aria-label="사진 빼기"
                            onClick={() => setImages((p) => p.filter((_, j) => j !== i))}
                          >
                            <svg viewBox="0 0 10 10" aria-hidden>
                              <path d="M2 2l6 6M8 2l-6 6" />
                            </svg>
                          </button>
                        </div>
                      );
                    const next = i === images.length;
                    return (
                      <div key={i} className={`cp-aip-photo-add${next ? '' : ' is-idle'}`}>
                        <button type="button" onClick={() => setCamOpen(true)} disabled={!next}>
                          <CameraIcon />사진 찍기
                        </button>
                        <button type="button" onClick={() => void pasteFromClipboard()} disabled={!next}>
                          <ClipboardIcon />클립보드
                        </button>
                        <button type="button" onClick={() => fileRef.current?.click()} disabled={!next}>
                          <UploadIcon />사진 올리기
                        </button>
                      </div>
                    );
                  })}
                </div>
                <input ref={camRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => { void addFiles(e.target.files); e.target.value = ''; }} />
                <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => { void addFiles(e.target.files); e.target.value = ''; }} />
                {clipMsg ? <p className="cp-aip-clip-msg" role="status">{clipMsg}</p> : <p className="cp-aip-sub cp-aip-center">사진을 복사했다면 Ctrl+V(⌘+V)로 바로 붙여 넣을 수 있어요.</p>}
              </div>
              <div className="cp-aip-block">
                <div className="cp-aip-label cp-aip-label--center">학년 <span className="cp-aip-sub">전체·공통도 이 학년에 포함해 읽어요</span></div>
                <div className="cp-aip-grades" role="radiogroup" aria-label="학년">
                  {[1, 2, 3, 4, 5, 6].map((g) => (
                    <button key={g} type="button" role="radio" aria-checked={grade === g} className={grade === g ? 'is-on' : ''} onClick={() => setGrade(g)}>
                      {g}학년
                    </button>
                  ))}
                </div>
              </div>
              <div className="cp-aip-block">
                <div className="cp-aip-label">
                  추가 요청 <span className="cp-aip-sub">선택</span>
                  <span className="cp-aip-count">{Array.from(extra).length}/{AI_PRESET_MAX_EXTRA}</span>
                </div>
                <textarea
                  className="cp-aip-textarea"
                  value={extra}
                  maxLength={AI_PRESET_MAX_EXTRA}
                  placeholder="예: 2쪽은 2학기 일정이에요. 방과후는 2타임까지 있어요."
                  onChange={(e) => setExtra(Array.from(e.target.value).slice(0, AI_PRESET_MAX_EXTRA).join(''))}
                />
              </div>
            </>
          ) : (
            <>
              <p className="cp-aip-caution">AI가 추출한 정보는 부정확할 수 있습니다. 적용 전 반드시 검토해주세요</p>
              <div className="cp-aip-result-top">
                <span className="cp-aip-pill">{grade}학년</span>
                <span className="cp-aip-sub">아래 내용 그대로 입력돼요. 고칠 곳을 고친 뒤 ‘반영’을 누르세요. 반영 전에는 저장되지 않아요.</span>
              </div>
              {notes ? <p className="cp-aip-notes">AI 참고: {notes}</p> : null}
              {calendar && props.kind === 'calendar' ? (
                <CalendarPreview value={calendar} onChange={setCalendar} />
              ) : null}
              {schedule ? <SchedulePreview value={schedule} onChange={setSchedule} /> : null}
              {tracks ? (
                <TracksPreview
                  value={tracks}
                  onChange={setTracks}
                  existing={props.kind === 'tracks' ? props.existingTrackNames ?? [] : []}
                />
              ) : null}
              {crossTopics ? (
                <CrossTopicsPreview
                  value={crossTopics}
                  onChange={setCrossTopics}
                  existing={props.kind === 'crossTopics' ? props.existingTopics ?? [] : []}
                />
              ) : null}
              {fixedPrograms && props.kind === 'fixedPrograms' ? (
                <FixedProgramBulkTable
                  ref={fixedPreviewRef}
                  workspaceId={props.workspaceId}
                  classOptions={props.classOptions}
                  initialDrafts={fixedPrograms}
                  seedFromCellCache={false}
                  emptyText="문서에서 찾은 행사가 없어요. 아래에서 직접 추가할 수 있어요."
                />
              ) : null}
            </>
          )}
          {error ? <p className="cp-aip-error" role="alert">{error}</p> : null}
        </div>
        <div className="cp-modal-footer cp-aip-footer">
          {hasResult ? (
            <>
              <button type="button" className="cp-btn-secondary" onClick={backToInput}>
                다시 읽기
              </button>
              <button type="button" className="cp-btn-primary cp-aip-primary" onClick={apply}>
                반영
              </button>
            </>
          ) : (
            <>
              <button type="button" className="cp-btn-secondary" onClick={requestClose} disabled={busy}>
                취소
              </button>
              {hasSaved ? (
                <button type="button" className="cp-btn-secondary cp-aip-resume" onClick={showPrevious} disabled={busy}>
                  기존 AI 입력자료 보기
                </button>
              ) : null}
              <button type="button" className="cp-btn-primary cp-aip-primary" onClick={() => void run()} disabled={busy || images.length === 0}>
                {busy ? <><span className="cp-aip-spin" aria-hidden />읽는 중…</> : 'AI로 읽기'}
              </button>
            </>
          )}
        </div>
        {camOpen ? (
          <AiCameraCapture
            onClose={() => setCamOpen(false)}
            onCapture={(f) => {
              setCamOpen(false);
              void addFileList([f]);
            }}
            onFallback={() => {
              setCamOpen(false);
              camRef.current?.click();
            }}
          />
        ) : null}
      </div>
    </div>
  );
};

export default AiPresetImportModal;

/* ---------------- 학사일정 ---------------- */
type HolidayStatus = 'match' | 'auto_only' | 'doc_only';
type CalendarEdit = {
  settings: AiCalendarApply['settings'];
  days: Array<{ key: string; day: string; kind: 'holiday' | 'closure'; note: string; status: HolidayStatus; include: boolean }>;
  programs: FixedProgramDraft[];
};

function buildCalendarEdit(
  r: AiCalendarResult,
  autoHolidays: (year: number) => Array<{ day: string; note: string }>
): CalendarEdit {
  const y = r.academic_year;
  const seenAuto = new Set<string>();
  const autos = [...autoHolidays(y), ...autoHolidays(y + 1)]
    .filter((h) => h.day)
    .filter((h) => !seenAuto.has(h.day) && !!seenAuto.add(h.day));
  const docByDay = new Map(r.days.map((d) => [d.day, d]));
  const autoDays = new Set(autos.map((a) => a.day));
  const days: CalendarEdit['days'] = [
    ...autos.map((a) => ({
      key: `a${a.day}`,
      day: a.day,
      kind: 'holiday' as const,
      note: a.note,
      status: (docByDay.has(a.day) ? 'match' : 'auto_only') as HolidayStatus,
      include: true,
    })),
    ...r.days
      .filter((d) => !autoDays.has(d.day))
      .map((d) => ({ key: `d${d.day}`, day: d.day, kind: d.kind, note: d.note, status: 'doc_only' as HolidayStatus, include: true })),
  ].sort((a, b) => a.day.localeCompare(b.day));
  return {
    settings: {
      academicYear: y,
      yearStartsOn: r.year_starts_on,
      yearEndsOn: r.year_ends_on,
      summerBreakStartsOn: r.summer_break_starts_on,
      summerBreakEndsOn: r.summer_break_ends_on,
      winterBreakStartsOn: r.winter_break_starts_on,
      winterBreakEndsOn: r.winter_break_ends_on,
    },
    days,
    programs: [],
  };
}

function calendarToApply(c: CalendarEdit): AiCalendarApply {
  const shown = (d: CalendarEdit['days'][number]) => !!d.day && !holidayRowHidden(d.day, c.settings);
  return {
    settings: c.settings,
    days: c.days
      .filter((d) => d.include && shown(d))
      .map((d) => ({ day: d.day, day_kind: 'holiday' as const, note: d.note.slice(0, 10) })),
    omittedDays: c.days.reduce<string[]>((acc, d) => {
      if (d.day && !shown(d) && !acc.includes(d.day)) acc.push(d.day);
      return acc;
    }, []),
    programs: [],
  };
}

function dayInClosedRange(day: string, start: string, end: string): boolean {
  return !!day && !!start && !!end && start <= end && day >= start && day <= end;
}

/** 여름·겨울방학(시작·끝이 모두 있을 때) 안의 날짜 */
function dayInVacation(day: string, s: AiCalendarApply['settings']): boolean {
  return (
    dayInClosedRange(day, s.summerBreakStartsOn, s.summerBreakEndsOn) ||
    dayInClosedRange(day, s.winterBreakStartsOn, s.winterBreakEndsOn)
  );
}

const STATUS_LABEL: Record<HolidayStatus, string> = {
  match: '일치',
  auto_only: '자동등록에만 있음(문서에 없음)',
  doc_only: '문서에만 있음',
};

const WEEKDAY_KO = ['일', '월', '화', '수', '목', '금', '토'];

function weekdayOfIso(iso: string): { label: string; weekend: boolean } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const dt = new Date(y, mo - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null;
  const w = dt.getDay();
  return { label: `(${WEEKDAY_KO[w]})`, weekend: w === 0 || w === 6 };
}

/** 목록·반영에서 빼는 날. 위 날짜가 비어 있으면 학년도 기본 범위(3/1~다음 해 2/28)로 본다. */
function holidayRowHidden(day: string, s: AiCalendarApply['settings']): boolean {
  if (!day) return false;
  const y = s.academicYear;
  const start = s.yearStartsOn || (y ? `${y}-03-01` : '');
  const end = s.yearEndsOn || (y ? `${y + 1}-02-28` : '');
  const outsideYear = (!!start && day < start) || (!!end && day > end);
  return dayInVacation(day, s) || !!weekdayOfIso(day)?.weekend || outsideYear;
}

const CalendarPreview: React.FC<{ value: CalendarEdit; onChange: (v: CalendarEdit) => void }> = ({
  value,
  onChange,
}) => {
  const s = value.settings;
  const setS = (patch: Partial<CalendarEdit['settings']>) => onChange({ ...value, settings: { ...s, ...patch } });
  const setDay = (key: string, patch: Partial<CalendarEdit['days'][number]>) =>
    onChange({ ...value, days: value.days.map((d) => (d.key === key ? { ...d, ...patch } : d)) });
  const visibleDays = useMemo(
    () => value.days.filter((d) => !holidayRowHidden(d.day, s)),
    [value.days, s.academicYear, s.yearStartsOn, s.yearEndsOn, s.summerBreakStartsOn, s.summerBreakEndsOn, s.winterBreakStartsOn, s.winterBreakEndsOn]
  );
  const counts = useMemo(() => {
    const c = { match: 0, auto_only: 0, doc_only: 0 };
    visibleDays.forEach((d) => {
      c[d.status] += 1;
    });
    return c;
  }, [visibleDays]);
  const dateIn = (v: string, on: (x: string) => void, label: string) => (
    <input type="date" className="cp-aip-in" aria-label={label} value={v} onChange={(e) => on(e.target.value)} />
  );
  const requiredField = (name: string, node: React.ReactNode) => (
    <label className="cp-aip-date-field">
      <span className="cp-aip-date-field-name">{name} <span className="required">*필수</span></span>
      {node}
    </label>
  );
  const optionalDate = (name: string, value: string, on: (x: string) => void) => (
    <div className="cp-aip-date-field">
      <span className="cp-aip-date-field-name cp-aip-date-field-name--split">
        <span>{name} <span className="optional">*선택</span></span>
        <button type="button" className="cp-btn-secondary cp-aip-date-clear" disabled={!value} onClick={() => on('')}>
          지우기
        </button>
      </span>
      {dateIn(value, on, name)}
    </div>
  );
  return (
    <>
      <section className="cp-aip-sec">
        <h4>학년도 · 방학</h4>
        <div className="cp-aip-grid2">
          {requiredField('학년도', <input type="number" className="cp-aip-in" aria-label="학년도" value={s.academicYear} onChange={(e) => setS({ academicYear: Number(e.target.value) || s.academicYear })} />)}
          <span />
          {requiredField('시작일', dateIn(s.yearStartsOn, (x) => setS({ yearStartsOn: x }), '시작일'))}
          {requiredField('종료일(종업·졸업)', dateIn(s.yearEndsOn, (x) => setS({ yearEndsOn: x }), '종료일(종업·졸업)'))}
          {requiredField('여름방학 시작', dateIn(s.summerBreakStartsOn, (x) => setS({ summerBreakStartsOn: x }), '여름방학 시작'))}
          {requiredField('여름방학 끝', dateIn(s.summerBreakEndsOn, (x) => setS({ summerBreakEndsOn: x }), '여름방학 끝'))}
          {optionalDate('겨울방학 시작', s.winterBreakStartsOn, (x) => setS({ winterBreakStartsOn: x }))}
          {optionalDate('겨울방학 끝', s.winterBreakEndsOn, (x) => setS({ winterBreakEndsOn: x }))}
        </div>
      </section>
      <section className="cp-aip-sec">
        <h4>
          공휴일 · 휴업일
          <span className="cp-aip-sub">
            일치 {counts.match} · 문서에만 {counts.doc_only} · 자동등록에만 {counts.auto_only}
          </span>
          <span className="cp-aip-sub">위 날짜를 바꾸면 목록이 바로 바뀌어요. 방학·토·일·시작일 전·종료일 다음은 빠지고, 반영은 보이는 날짜만 저장해요.</span>
        </h4>
        <table className="cp-aip-table">
          <thead>
            <tr><th>반영</th><th>날짜</th><th>이름</th><th>비교</th><th /></tr>
          </thead>
          <tbody>
            {visibleDays.map((d) => {
              const weekday = weekdayOfIso(d.day);
              return (
              <tr key={d.key} className={d.include ? '' : 'is-off'}>
                <td className="cp-aip-center"><RoundCheck checked={d.include} onChange={(v) => setDay(d.key, { include: v })} ariaLabel="반영" /></td>
                <td>
                  <span className={`cp-aip-date-cell${weekday?.weekend ? ' is-weekend' : ''}`}>
                    <input type="date" className="cp-aip-in" value={d.day} onChange={(e) => setDay(d.key, { day: e.target.value })} />
                    {weekday ? <span className="cp-aip-weekday">{weekday.label}</span> : null}
                  </span>
                </td>
                <td><input className="cp-aip-in" value={d.note} maxLength={10} onChange={(e) => setDay(d.key, { note: e.target.value })} /></td>
                <td><span className={`cp-aip-status is-${d.status}`}>{STATUS_LABEL[d.status]}</span></td>
                <td><button type="button" className="cp-aip-del" aria-label="삭제" onClick={() => onChange({ ...value, days: value.days.filter((x) => x.key !== d.key) })}>×</button></td>
              </tr>
              );
            })}
          </tbody>
        </table>
        <button
          type="button"
          className="cp-aip-add cp-aip-add--full"
          onClick={() => onChange({ ...value, days: [...value.days, { key: `n${Date.now()}`, day: '', kind: 'holiday', note: '', status: 'doc_only', include: true }] })}
        >
          ＋ 날짜 추가
        </button>
      </section>
    </>
  );
};

/* ---------------- 수업시간 ---------------- */
const SchedulePreview: React.FC<{ value: AiScheduleResult; onChange: (v: AiScheduleResult) => void }> = ({ value, onChange }) => {
  const setP = (i: number, patch: Partial<AiScheduleResult['periods'][number]>) =>
    onChange({ ...value, periods: value.periods.map((p, j) => (j === i ? { ...p, ...patch } : p)) });
  const afterRows = Math.max(0, 10 - value.periods.length);
  return (
    <>
      <section className="cp-aip-sec">
        <h4>교시별 시간 <span className="cp-aip-sub">활동 행은 만들지 않아요 · 최대 10행</span></h4>
        <table className="cp-aip-table">
          <thead><tr><th className="cp-aip-center">구분</th><th>시작</th><th>종료</th><th /></tr></thead>
          <tbody>
            {value.periods.map((p, i) => (
              <tr key={i}>
                <td className="cp-aip-strong cp-aip-center">{i + 1}교시</td>
                <td><input type="time" className="cp-aip-in" value={p.start_time} onChange={(e) => setP(i, { start_time: e.target.value })} /></td>
                <td><input type="time" className="cp-aip-in" value={p.end_time} onChange={(e) => setP(i, { end_time: e.target.value })} /></td>
                <td><button type="button" className="cp-aip-del" aria-label="삭제" onClick={() => onChange({ ...value, periods: value.periods.filter((_, j) => j !== i).map((x, j) => ({ ...x, period: j + 1 })) })}>×</button></td>
              </tr>
            ))}
            {Array.from({ length: afterRows }, (_, i) => (
              <tr key={`as${i}`} className="is-muted">
                <td className="cp-aip-strong cp-aip-center">방과후{i + 1}</td>
                <td colSpan={2} className="cp-aip-sub cp-aip-center">시간 없음</td>
                <td />
              </tr>
            ))}
          </tbody>
        </table>
        <div className="cp-aip-row cp-aip-row--col">
          <button type="button" className="cp-aip-add cp-aip-add--full" disabled={value.periods.length >= 10} onClick={() => onChange({ ...value, periods: [...value.periods, { period: value.periods.length + 1, start_time: '', end_time: '' }] })}>
            ＋ 교시 추가
          </button>
          <span className="cp-aip-sub">정규 교시 뒤 나머지 행은 방과후(시간 없음)로 기록돼요.</span>
        </div>
      </section>
      <section className="cp-aip-sec">
        <h4>요일별 기준 수업시수</h4>
        <div className="cp-aip-weekdays cp-aip-weekdays--center">
          {(['mon', 'tue', 'wed', 'thu', 'fri'] as const).map((k, i) => (
            <label key={k}>
              <span>{'월화수목금'[i]}</span>
              <input type="number" min={0} max={10} className="cp-aip-in cp-aip-in--num" value={value.weekday_periods[k]}
                onChange={(e) => onChange({ ...value, weekday_periods: { ...value.weekday_periods, [k]: Math.max(0, Math.min(10, Number(e.target.value) || 0)) } })} />
            </label>
          ))}
        </div>
      </section>
    </>
  );
};

/* ---------------- 편제 ---------------- */
const normTrackName = (v: string) => v.replace(/\s+/g, '');
const TracksPreview: React.FC<{ value: AiTrackRow[]; onChange: (v: AiTrackRow[]) => void; existing: string[] }> = ({ value, onChange, existing }) => {
  const set = (i: number, patch: Partial<AiTrackRow>) => onChange(value.map((t, j) => (j === i ? { ...t, ...patch } : t)));
  const existingSet = useMemo(() => new Set(existing.map(normTrackName)), [existing]);
  const status = value.map((t, i) => {
    const k = normTrackName(t.name);
    if (!k) return 'empty' as const;
    if (existingSet.has(k)) return 'keep' as const;
    if (value.slice(0, i).some((x) => normTrackName(x.name) === k)) return 'dup' as const;
    return 'new' as const;
  });
  const addCount = status.filter((x) => x === 'new').length;
  const keepCount = status.filter((x) => x === 'keep').length;
  const sum = value.reduce((a, t, i) => a + (status[i] === 'new' ? Number(t.criteria_hours) || 0 : 0), 0);
  return (
    <section className="cp-aip-sec">
      <h4>
        편제 및 기준시수
        <span className="cp-aip-sub">같은 이름의 기존 편제는 덮어쓰지 않고 그대로 두며, 새 이름만 추가해요</span>
      </h4>
      <div className="cp-aip-statline">
        <span className="cp-aip-tstat is-new">새로 추가 {addCount}</span>
        <span className="cp-aip-tstat is-keep">기존 유지 {keepCount}</span>
        <span className="cp-aip-sub">추가분 기준시수 합계 {sum}시간</span>
      </div>
      <table className="cp-aip-table cp-aip-table--tracks">
        <thead>
          <tr>
            <th>반영</th><th>편제명</th><th className="cp-aip-center">시간표명</th><th className="cp-aip-center">기준시수</th>
            <th className="cp-aip-center">1·2학기</th><th className="cp-aip-center">구분</th><th>출판사</th><th />
          </tr>
        </thead>
        <tbody>
          {value.map((t, i) => {
            const st = status[i];
            const off = st !== 'new';
            return (
              <tr key={i} className={off ? 'is-keep' : ''}>
                <td className="cp-aip-center">
                  <span className={`cp-aip-tstat is-${st === 'new' ? 'new' : 'keep'}`}>
                    {st === 'new' ? '새로 추가' : st === 'keep' ? '기존 유지(덮어쓰지 않음)' : st === 'dup' ? '중복(제외)' : '이름 없음'}
                  </span>
                </td>
                <td><input className="cp-aip-in" value={t.name} onChange={(e) => set(i, { name: e.target.value })} /></td>
                <td className="cp-aip-center"><input className="cp-aip-in cp-aip-in--short" maxLength={4} value={t.short_name} disabled={st === 'keep'} onChange={(e) => set(i, { short_name: e.target.value })} /></td>
                <td className="cp-aip-center"><input type="number" min={0} max={999} className="cp-aip-in cp-aip-in--num" value={t.criteria_hours} disabled={st === 'keep'} onChange={(e) => set(i, { criteria_hours: Math.max(0, Math.min(999, Number(e.target.value) || 0)) })} /></td>
                <td className="cp-aip-center">
                  <div className="cp-aip-seg">
                    <button type="button" disabled={st === 'keep'} className={t.term_scope === 'split' ? 'is-on' : ''} onClick={() => set(i, { term_scope: 'split' })}>구분</button>
                    <button type="button" disabled={st === 'keep'} className={t.term_scope === 'unified' ? 'is-on' : ''} onClick={() => set(i, { term_scope: 'unified' })}>통합</button>
                  </div>
                </td>
                <td className="cp-aip-center">
                  <select className="cp-aip-in cp-aip-in--cat" value={t.category} disabled={st === 'keep'} onChange={(e) => set(i, { category: e.target.value as AiTrackRow['category'] })}>
                    <option value="regular">정규</option>
                    <option value="other">기타</option>
                  </select>
                </td>
                <td><input className="cp-aip-in" value={t.publisher} placeholder="—" disabled={st === 'keep'} onChange={(e) => set(i, { publisher: e.target.value })} /></td>
                <td className="cp-aip-center"><button type="button" className="cp-aip-del" aria-label="삭제" onClick={() => onChange(value.filter((_, j) => j !== i))}>×</button></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <button type="button" className="cp-aip-add cp-aip-add--full" onClick={() => onChange([...value, { name: '', short_name: '', criteria_hours: 0, term_scope: 'split', category: 'regular', publisher: '' }])}>
        ＋ 편제 추가
      </button>
    </section>
  );
};

const CrossTopicsPreview: React.FC<{
  value: AiCrossTopicRow[];
  onChange: (v: AiCrossTopicRow[]) => void;
  existing: string[];
}> = ({ value, onChange, existing }) => {
  const set = (i: number, patch: Partial<AiCrossTopicRow>) => onChange(value.map((t, j) => (j === i ? { ...t, ...patch } : t)));
  const existingSet = useMemo(() => new Set(existing.map((t) => t.trim())), [existing]);
  const hours = (v: string) => Math.max(0, Math.min(999, Math.floor(Number(v) || 0)));
  return (
    <section className="cp-aip-sec">
      <h4>
        범교과 주제
        <span className="cp-aip-sub">같은 주제어는 시수만 바꾸고, 새 주제어는 추가해요</span>
      </h4>
      <table className="cp-aip-table">
        <thead>
          <tr>
            <th>구분</th>
            <th>주제어</th>
            <th className="cp-aip-center">1학기</th>
            <th className="cp-aip-center">2학기</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {value.map((t, i) => {
            const known = existingSet.has(t.topic.trim());
            return (
              <tr key={i}>
                <td className="cp-aip-center">
                  <span className={`cp-aip-tstat is-${known ? 'keep' : 'new'}`}>{known ? '시수 갱신' : '새로 추가'}</span>
                </td>
                <td>
                  <input className="cp-aip-in" maxLength={40} value={t.topic} onChange={(e) => set(i, { topic: e.target.value })} />
                </td>
                <td className="cp-aip-center">
                  <input type="number" min={0} max={999} className="cp-aip-in cp-aip-in--num" value={t.term1_hours} onChange={(e) => set(i, { term1_hours: hours(e.target.value) })} />
                </td>
                <td className="cp-aip-center">
                  <input type="number" min={0} max={999} className="cp-aip-in cp-aip-in--num" value={t.term2_hours} onChange={(e) => set(i, { term2_hours: hours(e.target.value) })} />
                </td>
                <td className="cp-aip-center">
                  <button type="button" className="cp-aip-del" aria-label="삭제" onClick={() => onChange(value.filter((_, j) => j !== i))}>
                    ×
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <button type="button" className="cp-aip-add cp-aip-add--full" onClick={() => onChange([...value, { topic: '', term1_hours: 0, term2_hours: 0 }])}>
        ＋ 주제 추가
      </button>
    </section>
  );
};
